// src/components/BillingOverlays/BillingOverlays.jsx
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import SnackBar from "../SnackBar/SnackBar";
import TrialExpiredModal from "../TrialExpiredModal/TrialExpiredModal";
import PaymentExpiredModal from "../PaymentExpiredModal/PaymentExpiredModal";
import BoardLimitModal from "../BoardLimitModal/BoardLimitModal";

import { getPricingForBoards, getUkrainianDayWord } from "../../lib/billingUtils";

/**
 * Action Gate:
 * - Автоматично показує модалки при завантаженні (як раніше)
 * - І повторно показує їх по дії (клік “Додати борд”),
 *   навіть якщо користувач раніше закрив.
 *
 * Props:
 * - company, activeBoards, archivedBoards, onStatusChange (як було)
 * - billingAction: { type: "create_board" } | null
 * - clearBillingAction: () => void
 * - createBoard: async () => void  (реальний create-board API)
 */
export default function BillingOverlays({
  company,
  activeBoards,
  archivedBoards,
  onStatusChange,

  // Action gate props
  billingAction,
  clearBillingAction,
  createBoard,
  updateBoardStatus
}) {
  const { t } = useTranslation();
  const activeCount = activeBoards?.length ?? 0;

  // керований “який оверлей зараз відкритий”
  // "trial_expired" | "payment_expired" | "board_limit" | null
  const [overlay, setOverlay] = useState(null);

  const [pendingAction, setPendingAction] = useState(null);

  const pricing = useMemo(() => {
    const planLimit = company?.active_boards_in_plan;
    const status = company?.payment_status;
    return getPricingForBoards(activeCount, planLimit, status);
  }, [
    activeCount,
    company?.active_boards_in_plan,
    company?.payment_status, // ✅ важливо: щоб trial → effectiveLimit = 6 застосувався коректно
  ]);

  const lastBilledPrice = useMemo(() => {
    const planLimit = company?.active_boards_in_plan;
    const status = company?.payment_status;
    return getPricingForBoards(activeCount, planLimit, status).currentPrice;
  }, [
    company?.active_boards_in_plan,
    company?.payment_status, // ✅ щоб не було різних трактувань
    activeCount,
  ]);

  // ✅ 1) Авто-відкриття при завантаженні / зміні статусу (як було)
  useEffect(() => {
    if (!company) return;

    if (company.payment_status === "trial_expired") {
      setOverlay("trial_expired");
      return;
    }
    if (company.payment_status === "payment_expired") {
      setOverlay("payment_expired");
      return;
    }

    // якщо статус ок — не відкриваємо нічого автоматом
    // (і не закриваємо примусово, щоб не миготіло під час оновлень)
  }, [company?.payment_status, company]);

  // ✅ 2) Gate-логіка по дії користувача (натиснула “Додати борд”)
  useEffect(() => {
    if (!billingAction) return;
    if (!company) return;

    // очищаємо, щоб не повторювалося при наступних рендерах
    clearBillingAction?.();

    const isCreate = billingAction.type === "create_board";
    const isUpdate = billingAction.type === "update_board_status";

    if (!isCreate && !isUpdate) return;

    // 1) expired -> модалки
    if (company.payment_status === "trial_expired") {
      setOverlay("trial_expired");
      setPendingAction(null);
      return;
    }
    if (company.payment_status === "payment_expired") {
      setOverlay("payment_expired");
      setPendingAction(null);
      return;
    }

    // 2) якщо це update-status НЕ на "active" — це не повинно проходити через gate
    // (але в нашій моделі ми сюди і не повинні потрапляти)
    if (isUpdate && billingAction.newStatus !== "active") {
      setPendingAction(null);
      return;
    }

    // 3) upgrade needed -> board_limit
    if (pricing.willUpgradeOnNextBoard) {
      setOverlay("board_limit");
      setPendingAction(billingAction);
      return;
    }

    // 4) allow action
    (async () => {
      try {
        if (isCreate) {
          await createBoard?.();
          return;
        }

        if (isUpdate) {
          const { boardId, newStatus } = billingAction;
          if (!boardId || !newStatus) return;
          await updateBoardStatus?.(boardId, newStatus);
          return;
        }
      } catch (e) {
        console.error("Billing action failed:", e);
      }
    })();
  }, [
    billingAction,
    company,
    pricing.willUpgradeOnNextBoard,
    clearBillingAction,
    createBoard,
    updateBoardStatus,
  ]);

  if (!company) return null;

  return (
    <>
      {/* trial snackbar (не модалка) */}
      {company.payment_status === "trial" && company.days_left_in_trial > 0 && (
        <SnackBar
          open={true}
          message={t('trial_banner.text', {
            total: company.trial_length,
            days_word: getUkrainianDayWord(company.trial_length),
            left: company.days_left_in_trial,
            days_left_word: getUkrainianDayWord(company.days_left_in_trial),
          })}
          actionText={t('trial_banner.choose_plan')}
          actionLink="/billing"
          onClose={() => console.log("closed")}
        />
      )}

      {/* trial expired */}
      {overlay === "trial_expired" && (
        <TrialExpiredModal
          open={true}
          companySlug={company.slug}
          activeBoards={activeBoards}
          archivedBoards={archivedBoards}
          price={pricing.requiredNow.price}
          onStatusChange={onStatusChange}
          onClose={() => setOverlay(null)}
        />
      )}

      {/* payment expired */}
      {overlay === "payment_expired" && (
        <PaymentExpiredModal
          companySlug={company.slug}
          activeBoardsInPlan={company.active_boards_in_plan}
          activeBoards={activeBoards}
          archivedBoards={archivedBoards}
          price={lastBilledPrice}
          onStatusChange={onStatusChange}
          onClose={() => setOverlay(null)}
        />
      )}

      {/* upgrade needed to create next board */}
      {overlay === "board_limit" && (
        <BoardLimitModal
          open={true}
          onClose={() => {
            setOverlay(null);
            setPendingAction(null);
          }}
          currentPrice={pricing.currentPrice}
          nextBoardPrice={pricing.nextPlanPrice}
          companySlug={company.slug}
          newPlanLimit={pricing.nextPlanLimit}
          pendingAction={pendingAction}
          onDone={onStatusChange}
        />
      )}
    </>
  );
}